<template>
  <CollapsibleCard
    :editable="editable && canBeEdited"
    :editing="editing"
  >
    <template v-slot:header>
      <h5 :style="{ textTransform: 'initial', margin: 'unset' }">
        KURSER FRÅN GRUNDUTBILDNING {{ `${!reform ? '' : `( ${reform} )`}` }}
      </h5>
    </template>
    <template v-slot:info>{{ getHeaderInfo }}</template>
    <template v-slot:detailsinfo></template>
    <template v-slot:editinfo>
      Systemet har identifierat dessa kurser och meriter i de uppladdade betygen och intygen.
    </template>

    <template v-slot:view>
      <ApplicationEducationView
        :getItems="getItems"
        :itemsPerPage="itemsPerPage"
        :selectItems="selectItems"
        :certificateIsCreated="certificateIsCreated"
        :certificateIsApproved="certificateIsApproved"
        :propEducation="merge(selectedApplication.education, {
          personId: get(person, 'id', null),
          apprenticeship: getApprenticeship
        })"
        @confirm-education="confirmEducation"
        @saved="saved"
      />
    </template>

    <template v-slot:edit>
      <TheReformSelect
        :style="{ width: '300px' }"
        :reform="reform"
        @selected-reform="reform = $event"
      />

      <ApplicationEducationEdit
        :getItems="getItems"
        :itemsPerPage="itemsPerPage"
        :selectItems="selectItems"
        :certificateIsCreated="certificateIsCreated"
        :certificateIsApproved="certificateIsApproved"
        :reform="reform"
        @saved="saved"
        @confirm-education="confirmEducation"
        @on-cancel="reform = $event"
        ref="edit"
      />
    </template>

    <template v-slot:cancel>
      <v-btn
        color="secondary"
        @click="$refs.edit.cancel()"
      >
        <v-icon>$sl-cancel</v-icon>Avbryt
      </v-btn>
    </template>

    <template v-slot:save>
      <v-btn
        data-cy="save-application"
        @click="$refs.edit.save()"
      >
        <v-icon>$sl-check</v-icon>Spara
      </v-btn>
    </template>

    <v-dialog
      v-model="edModal"
      :max-width="720"
    >
      <EducationModal
        :title="addTitle"
        :propId="selectedApplication.id"
        :propEducation="merge(selectedApplication.education, {
          application: {
            id: selectedApplication.id, 
            stateId: selectedApplication.stateId,
            transferOldProfession: selectedApplication.transferOldProfession
          },
          apprenticeship: getApprenticeship
        })"
        :propCourses="selectedApplication.applicationCourses"
        :documents="selectedApplication.applicationDocuments"
        :person="selectedApplication"
        :propProfession="applicationProfession"
        :propMachines="selectedApplication.professionOrientations"
        @close="edModal = false; refresh"
        @deleted-education="refresh"
        @refuse="edModal = false; refresh; $emit('refuse')"
        :mode="edMode"
      />
    </v-dialog>

  </CollapsibleCard>
</template>

<script>
import {pageSize} from '@/store/constants'
import MessageBox from '@/components/common/MessageBox.vue'
import CollapsibleCard from '@/components/common/CollapsibleCard'
import EducationModal from '@/components/common/Education/EducationModal'
import {RulesMixin, UserRules, DownloadsMixin} from '@/components/mixins'
import {mapGetters, mapActions} from 'vuex'
import API from '@/services/api'
import filter from 'lodash/filter'
import get from 'lodash/get'
import merge from 'lodash/merge'
import ApplicationEducationView from './ApplicationEducationView.vue'
import ApplicationEducationEdit from './ApplicationEducationEdit.vue'
import orderBy from 'lodash/orderBy'
import { find } from 'lodash'
import TheReformSelect from '@/components/dialog/copy-paste/TheReformSelect.vue'

// @group Application
// ApplicationEducation component
export default {
  name: 'ApplicationEducation',

  mixins: [RulesMixin, UserRules, DownloadsMixin],

  components: {
    CollapsibleCard,
    MessageBox,
    EducationModal,
    ApplicationEducationView,
    ApplicationEducationEdit,
    TheReformSelect
  },
  props: {
    //has title
    title: {
      // `String`
      type: String,
      // The default value is: `Skicka meddelande?`
      default: 'Skicka meddelande?'
    },
    
    editable: {
      type: Boolean,
      default: false
    },
  },

  data: () => ({
    itemsPerPage: pageSize,
    editing: null,
    showSave: true,
    edModal: false,
    edMode: '',
    addTitle: '',
    edModalObj: {},
    profession: {},

    selectItems: [
      {
        name: 'Godkänd',
        approved: true
      },
      {
        name: 'Ej godkänd',
        approved: false
      },
      {
        name: 'Saknas',
        approved: null
      }
    ],
    selectedItem: [],
    person: null,
    reform: null
  }),

  created() {
    // this.init()
    this.getPerson()
  },

  watch: {
    getItems: {
      handler(value) {
        this.reform = (value[0] || {}).reform || 'Gy11'
      },
      immediate: true
    }
  },

  computed: {
    ...mapGetters('application', [
      'selectedApplication',
      'applicationProfession',
      'canBeEdited',
      'approvedStateByCompany'
    ]),

    getApprenticeship() {
      const apprenticeships = get(this, 'person.apprenticeships', [])
      if ( !apprenticeships.length ) return null

      const { professionId, educationId } = get(this, 'selectedApplication', null)
      
      return find(orderBy(apprenticeships, ['id'], ['desc']), function(apprenticeship) {
        return get(apprenticeship, 'profession.id', '') === professionId && get(apprenticeship, 'educationId', '') === educationId
      })
    },

    isDisabled() {
      if (
        this.approvedStateByCompany == true &&
        this.certificateIsApproved == false &&
        this.certificateIsCreated == false
      ) {
        return false
      }
      return true
    },

    getItems() {
      const courses = get(this, 'selectedApplication.applicationCourses', [])

      let items = []
      if (courses.length > 0) {
        courses.forEach(e => {
          items.push({
            id: e.id,
            courseId: e.courseId,
            approved: e.approved,
            ocrApproved: e.ocrApproved,
            apprenticeApproved: e.apprenticeApproved,
            source: e.source,
            courseCode: e.course.courseCode,
            reform: e.course.reform,
            courseName: this.printCourseName(e.course)
          })
        })
      }
      return items
    },

    certificateIsApproved() {
      const certificateApproved = get(this, 'selectedApplication.education.basicEducationCertificate.approved', null)

      if (certificateApproved) {
        return true
      }
      return false
    },

    certificateIsCreated() {
      const certificateId = get(this, 'selectedApplication.education.basicEducationCertificate.createdById', null)

      if (certificateId) {
        return true
      }
      return false
    },

    dissableApproveBtn() {
      const state = get(this, 'selectedApplication.state.state', null)

      return Boolean(state === 'basicEducationCertificateIssued')
    },

    getHeaderInfo() {
      if (get(this, 'selectedApplication.allCoursesApproved', false)) {
        return 'Grundutbildning verkar vara komplett'
      }
      return 'Grundutbildning verkar sakna vissa kurser'
    }
  },

  methods: {
    ...mapActions('application', ['setSelectedApplication', 'updateSelectedApplication']),

    saved() {
      this.refresh()
      this.editing = false
      this.$emit('saved')
    },

    filter,

    get,

    merge,

    getProfession() {
      this.profession = this.applicationProfession
    },

    refresh() {
      API.getApplication(this.selectedApplication.id)
        .then(res => {
          this.setSelectedApplication(res.data)
        })
        .catch(error => {
          console.log('There was an error:', error)
        })
    },

    confirmEducation({edModal, edMode, addTitle}) {
      this.edModal = edModal
      this.edMode = edMode
      this.addTitle = addTitle
    },

    getPerson() {
      const personId = this.selectedApplication?.personId
      if(! personId) return

      API.getPerson(personId)
        .then(({ data }) => {
          this.person = data
          this.$emit('get-notes', this.person?.notes || [])
        })
        .catch((err) => console.log('Fetching person resulted in an error, reason >>>', err))
    },

    printCourseName(course) {
     return get(course, 'use_this_text_instead_of_orderitem_on_order', null) || get(course, 'courseName', '')
    },
  }
}
</script>

<style>
.Oval {
  width: 16px;
  height: 16px;
  margin: 2px 8px 15px 0px;
  border-radius: 50%;
  float: left;
}

.Oval.green {
  background-color: rgba(219, 238, 186) !important;
}

.Oval.red {
  background-color: rgba(209, 100, 90, 0.8) !important;
}

.missing {
  font-style: italic;
  color: rgba(59, 60, 58, 0.5);
}
</style>
